import * as React from 'react';
import { useEffect, useMemo, useState } from 'react';
import {
  AlertCircle,
  Box,
  Check,
  Download,
  EllipsisVertical,
  Euro,
  Info,
  Plus,
} from 'lucide-react';
import QRCodeStyling from 'qr-code-styling';

import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useIsMobile } from '@/hooks/use-mobile';
import i18n from '@/utils/i18n';
import { download } from '@/utils/download';

import { ContainerType } from '../ContainerComponents/ContainerPage';
import FeedbackBanner from '../../components/FeedbackBanner';
import { ContainerTypeLabelGenerationModal } from './ContainerTypeLabelGenerationModal';
import ContainerTypesFilterBar from './editContainerInfoNew/ContainerTypesFilterBar';
import ContainerTypesTable from './editContainerInfoNew/ContainerTypesTable';
import ContainerTypeEditorPanel, {
  SYSTEM_TYPE_BY_TAB,
  type EditorTab,
} from './editContainerInfoNew/ContainerTypeEditorPanel';
import MobileContainerTypes from './editContainerInfoNew/MobileContainerTypes';
import SegmentedControl from './editContainerInfoNew/SegmentedControl';
import { formatEurReference } from './editContainerInfoNew/ContainerPriceEditor';
import {
  buildFacetOptions,
  type FacetKey,
} from './editContainerInfoNew/containerTypeFilters';
import {
  useContainerTypesAdmin,
  DRAFT_ID,
} from './editContainerInfoNew/useContainerTypesAdmin';
import {
  WARNING_ACCENT,
  type PriceFieldDef,
  type SystemType,
} from './editContainerInfoNew/constants';

export const CONTAINER_TYPE_QR_CODE_PREFIX = 'HTTP://VYT.TO/CT/';

async function downloadQrCode(id: number) {
  const qrCode = new QRCodeStyling({
    width: 200,
    data: `${CONTAINER_TYPE_QR_CODE_PREFIX}${id}`,
    height: 200,
    dotsOptions: { color: '#000000', type: 'square' },
    imageOptions: { crossOrigin: 'anonymous' },
    backgroundOptions: { color: '#FFFFFF' },
    margin: 10,
    qrOptions: { errorCorrectionLevel: 'Q' },
  });
  const blob: any = await qrCode.getRawData('png');
  download(blob, `container_type_${id}_qr_code.png`);
}

/**
 * Redesigned "Container types" screen (handoff v2, frame 1a + mobile 3a):
 * data table with a Details/Pricing mode toggle, faceted filters, a 560px
 * edit sheet with Information · Open-system pricing · Event pricing tabs, a
 * draft-based create flow, and a purpose-built <768px mobile layout. Data is
 * served by the colugo GraphQL admin API (no REST endpoints).
 */
export default function EditContainerInfoNew() {
  const isMobile = useIsMobile();
  const admin = useContainerTypesAdmin({ isMobile });

  const [mode, setMode] = useState<'details' | 'pricing'>('details');
  const [systemType, setSystemType] = useState<SystemType>('OpenSystem');
  const [sheetTab, setSheetTab] = useState<EditorTab>('info');
  const [openContainerId, setOpenContainerId] = useState<number | null>(null);
  const [containerTypeForLabelModal, setContainerTypeForLabelModal] =
    useState<ContainerType | null>(null);

  const facetOptions = (key: FacetKey) =>
    buildFacetOptions(key, {
      containerClasses: admin.containerClasses,
      groupOptions: admin.groupOptions,
      countries: admin.countries,
      sizes: admin.facets.sizes,
      brandings: admin.facets.brandings,
      legalOwners: admin.facets.legalOwners,
    });

  // --- editor wiring (draft-aware) ---
  const isDraftOpen = admin.draft != null;
  // getRowById serves edited rows first, then any row seen this session — the
  // sheet keeps working while the underlying page flips or filters change.
  const openContainer = useMemo(
    () => (openContainerId != null ? admin.getRowById(openContainerId) : null),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [openContainerId, admin.getRowById],
  );
  const editorItem = isDraftOpen ? admin.draft : openContainer;
  const editorId = isDraftOpen ? DRAFT_ID : openContainer?.id;

  useEffect(() => {
    // Close the sheet if the open container was never loaded (edge case).
    if (openContainerId != null && openContainer == null && !admin.isLoading) {
      setOpenContainerId(null);
    }
  }, [openContainerId, openContainer, admin.isLoading]);

  const openContainerType = (ct: ContainerType) => {
    admin.closeDraft();
    setOpenContainerId(ct.id);
    setSheetTab(
      mode === 'pricing' ? (systemType === 'Event' ? 'event' : 'open') : 'info',
    );
  };

  const openNewDraft = () => {
    setOpenContainerId(null);
    setSheetTab('info');
    admin.openDraft();
  };

  const closeEditor = () => {
    if (isDraftOpen) admin.closeDraft();
    setOpenContainerId(null);
  };

  const handleTabChange = (tab: EditorTab) => {
    setSheetTab(tab);
    // Selecting a pricing tab also drives the page-level system type.
    if (tab !== 'info') setSystemType(SYSTEM_TYPE_BY_TAB[tab]);
  };

  const handleCreate = async () => {
    const createdId = await admin.createDraft();
    if (createdId != null) {
      setOpenContainerId(createdId);
      setSheetTab('info');
    }
  };

  const priceAccessors = {
    getRaw: (currency: string, def: PriceFieldDef): string => {
      if (isDraftOpen) {
        const prices: any = admin.getDraftPrices(currency);
        return String(prices?.[def.draftKey ?? ''] ?? '0');
      }
      if (editorId == null) return '0';
      if (def.source === 'overdue') {
        const price: any = admin.getOverduePrice(currency, editorId);
        return String(price?.[def.key] ?? '0');
      }
      return admin.getStoreRaw(editorId, currency, def.key);
    },
    setRaw: (currency: string, def: PriceFieldDef, raw: string) => {
      if (isDraftOpen) {
        if (def.draftKey) admin.setDraftPrice(currency, def.draftKey, raw);
        return;
      }
      if (editorId == null) return;
      if (def.source === 'overdue') {
        admin.setOverdueRaw(currency, editorId, def.key, raw);
        return;
      }
      admin.setStoreRaw(editorId, currency, def.key, raw);
    },
  };

  const getEurReference = (ct: ContainerType, def: PriceFieldDef): string => {
    const raw =
      def.source === 'overdue'
        ? String((admin.getOverduePrice('EUR', ct.id) as any)?.[def.key] ?? '0')
        : admin.getStoreRaw(ct.id, 'EUR', def.key);
    return formatEurReference(def, raw);
  };

  const changedCount = admin.dirtyIds.length;
  const isDirty = changedCount > 0;
  const saveHint = isDirty
    ? changedCount === 1
      ? i18n.t('EditContainerInfo:unsavedChange')
      : i18n.t('EditContainerInfo:unsavedChanges', { count: changedCount })
    : i18n.t('EditContainerInfo:allSaved');
  const saveDisabled =
    admin.isUpdating || admin.duplicateSkus.length > 0 || !isDirty;

  if (admin.isLoading) {
    return (
      <div className="tw:p-8 tw:text-sm tw:text-muted-foreground">
        {i18n.t('Common:loading')}
      </div>
    );
  }

  const editorPanel = (
    <ContainerTypeEditorPanel
      item={editorItem}
      isDraft={isDraftOpen}
      variant={isMobile ? 'mobile' : 'desktop'}
      tab={sheetTab}
      onTabChange={handleTabChange}
      onClose={closeEditor}
      language={admin.language}
      disabled={!admin.isAdministrator}
      containerClasses={admin.containerClasses}
      containerTypeGroups={admin.groupOptions}
      countries={admin.countries}
      brandings={admin.allBranding}
      legalOwners={admin.allLegalOwner}
      equivalenceOptions={admin.equivalenceOptions}
      duplicateSkus={admin.duplicateSkus}
      displayName={admin.displayName}
      onField={(attr, value) =>
        editorId != null && admin.handleField(editorId, attr, value)
      }
      onCountryToggle={(code) =>
        editorId != null && admin.handleCountryToggle(editorId, code)
      }
      onEquivalentToggle={(typeId) =>
        editorId != null && admin.handleEquivalentToggle(editorId, typeId)
      }
      onAddBranding={(value) =>
        editorId != null &&
        admin.updateContainerType(editorId, 'branding', value)
      }
      onAddLegalOwner={(value) =>
        editorId != null &&
        admin.updateContainerType(editorId, 'legalOwner', value)
      }
      onReorder={(direction) =>
        openContainer != null && admin.reorder(openContainer.id, direction)
      }
      onUploadImage={() => editorId != null && admin.openFileSelector(editorId)}
      priceAccessors={priceAccessors}
      draftEventPricingNote
      onDownloadQr={() =>
        openContainer != null && downloadQrCode(openContainer.id)
      }
      onPrintLabel={() =>
        openContainer != null && setContainerTypeForLabelModal(openContainer)
      }
      saveHint={saveHint}
      saveDisabled={saveDisabled}
      onSave={admin.save}
      createDisabled={!admin.canCreateDraft}
      creating={admin.draftSaving}
      onCreate={handleCreate}
    />
  );

  const labelModal = containerTypeForLabelModal && (
    <ContainerTypeLabelGenerationModal
      containerType={containerTypeForLabelModal}
      onClose={() => setContainerTypeForLabelModal(null)}
    />
  );

  const feedbackBanner = <FeedbackBanner path="edit-container-types" />;

  if (isMobile) {
    return (
      <>
        <MobileContainerTypes
          rows={admin.rows}
          totalCount={admin.totalCount}
          unfilteredCount={admin.unfilteredCount}
          hasMore={admin.hasMore}
          onLoadMore={admin.loadMore}
          feedbackSlot={feedbackBanner}
          filters={admin.filters}
          onFiltersChange={admin.setFilters}
          facetOptions={facetOptions}
          dirtyIds={admin.dirtyIds}
          displayName={admin.displayName}
          isAdministrator={admin.isAdministrator}
          isUpdating={admin.isUpdating}
          saveDisabled={saveDisabled}
          editorOpen={editorItem != null}
          onOpen={openContainerType}
          onNew={openNewDraft}
          onExport={() => admin.exportCsv()}
          onSave={admin.save}
          onDiscard={admin.discard}
        />
        {editorPanel}
        {labelModal}
      </>
    );
  }

  return (
    <div className="tw:flex tw:max-w-[1400px] tw:flex-col tw:gap-6 tw:p-4 tw:pb-16">
      {feedbackBanner}

      {/* 1. Page header */}
      <div className="tw:flex tw:items-start tw:justify-between tw:gap-4">
        <div className="tw:flex tw:flex-col tw:gap-1.5">
          <h1 className="tw:text-3xl tw:font-semibold tw:tracking-tight">
            {i18n.t('EditContainerInfo:pageTitle')}
          </h1>
          <p className="tw:text-sm tw:text-muted-foreground">
            {i18n.t('EditContainerInfo:pageSubtitle')}
          </p>
        </div>
        <div className="tw:flex tw:flex-none tw:gap-2">
          {admin.isAdministrator && (
            <Button onClick={openNewDraft}>
              <Plus size={15} />
              {i18n.t('EditContainerInfo:newContainerType')}
            </Button>
          )}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="outline"
                size="icon"
                aria-label={i18n.t('EditContainerInfo:moreActions')}
              >
                <EllipsisVertical size={16} />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="tw:min-w-[210px]">
              <DropdownMenuItem onClick={() => admin.exportCsv()}>
                <Download size={15} />
                {i18n.t('EditContainerInfo:exportContainerTypes')}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {/* 2. Mode toggle */}
      <SegmentedControl
        className="tw:self-start"
        options={[
          {
            value: 'details' as const,
            label: i18n.t('EditContainerInfo:modeDetails'),
            icon: <Box size={15} />,
          },
          {
            value: 'pricing' as const,
            label: i18n.t('EditContainerInfo:modePricing'),
            icon: <Euro size={15} />,
          },
        ]}
        value={mode}
        onChange={setMode}
      />

      {/* 3. Contextual note */}
      {mode === 'details' ? (
        <div className="tw:flex tw:items-start tw:gap-2.5 tw:rounded-md tw:border tw:border-border tw:bg-muted tw:px-3.5 tw:py-3">
          <Info
            size={16}
            className="tw:mt-0.5 tw:flex-none tw:text-muted-foreground"
          />
          <p className="tw:text-sm tw:leading-relaxed tw:text-muted-foreground">
            <b className="tw:font-semibold tw:text-foreground">
              {i18n.t('EditContainerInfo:calloutTitle')}
            </b>{' '}
            — {i18n.t('EditContainerInfo:calloutText')}
          </p>
        </div>
      ) : (
        <div className="tw:flex tw:items-center tw:justify-between tw:gap-3.5 tw:rounded-md tw:border tw:border-border tw:bg-muted tw:px-3.5 tw:py-3">
          <div className="tw:flex tw:items-start tw:gap-2.5">
            <Info
              size={16}
              className="tw:mt-0.5 tw:flex-none tw:text-muted-foreground"
            />
            <p className="tw:text-sm tw:leading-relaxed tw:text-muted-foreground">
              {i18n.t('EditContainerInfo:pricingModeNote')}
            </p>
          </div>
          <SegmentedControl
            className="tw:flex-none tw:bg-background"
            size="sm"
            options={[
              {
                value: 'OpenSystem' as const,
                label: i18n.t('EditContainerInfo:openSystem'),
              },
              {
                value: 'Event' as const,
                label: i18n.t('EditContainerInfo:event'),
              },
            ]}
            value={systemType}
            onChange={setSystemType}
          />
        </div>
      )}

      {/* 4. Filter bar */}
      <ContainerTypesFilterBar
        filters={admin.filters}
        onFiltersChange={admin.setFilters}
        language={admin.language}
        onLanguageChange={admin.setLanguage}
        facetOptions={facetOptions}
      />

      <Separator />

      {/* 5. Results toolbar + table + pagination */}
      <ContainerTypesTable
        rows={admin.rows}
        totalCount={admin.totalCount}
        unfilteredCount={admin.unfilteredCount}
        pageIndex={admin.pageIndex}
        pageCount={admin.pageCount}
        onPageChange={admin.setPageIndex}
        mode={mode}
        systemType={systemType}
        isAdministrator={admin.isAdministrator}
        groupNameById={admin.groupNameById}
        dirtyIds={admin.dirtyIds}
        displayName={admin.displayName}
        getEurReference={getEurReference}
        onOpen={openContainerType}
        onDownloadQr={(ct) => downloadQrCode(ct.id)}
        onPrintLabel={(ct) => setContainerTypeForLabelModal(ct)}
        onBulkArchive={admin.bulkArchive}
        onExport={(rows) => admin.exportCsv(rows)}
      />

      {/* 6. Duplicate-SKU alert */}
      {admin.duplicateSkus.length > 0 && (
        <div
          className="tw:flex tw:items-center tw:gap-2.5 tw:rounded-md tw:border tw:px-3.5 tw:py-3"
          style={{
            backgroundColor:
              'color-mix(in oklab, var(--destructive) 8%, var(--background))',
            borderColor:
              'color-mix(in oklab, var(--destructive) 30%, transparent)',
          }}
        >
          <AlertCircle size={16} className="tw:flex-none tw:text-destructive" />
          <span className="tw:text-sm tw:text-foreground">
            {i18n.t('EditContainerInfo:duplicateSkuAlert', {
              skus: admin.duplicateSkus.join(', '),
            })}
          </span>
        </div>
      )}

      {/* 7. Floating save bar */}
      {isDirty && (
        <div className="tw:fixed tw:bottom-6 tw:left-1/2 tw:z-40 tw:flex tw:-translate-x-1/2 tw:items-center tw:gap-3.5 tw:rounded-full tw:border tw:border-border tw:bg-popover tw:py-2 tw:pl-4.5 tw:pr-2.5 tw:shadow-lg">
          <span
            className="tw:inline-block tw:size-2 tw:rounded-full"
            style={{ backgroundColor: WARNING_ACCENT }}
          />
          <span className="tw:text-sm tw:font-medium">{saveHint}</span>
          <span className="tw:h-5 tw:w-px tw:bg-border" />
          <Button variant="ghost" size="sm" onClick={admin.discard}>
            {i18n.t('Common:discard')}
          </Button>
          <Button size="sm" onClick={admin.save} disabled={saveDisabled}>
            <Check size={15} />
            {i18n.t('Common:saveChanges')}
          </Button>
        </div>
      )}

      {/* 8. Edit sheet */}
      {editorPanel}

      {labelModal}
    </div>
  );
}
